import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container, Typography, Box, Grid, CircularProgress,
  Chip, Card, CardContent, CardMedia, CardActionArea, Alert, Tooltip,
  Divider, Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PeopleIcon from '@mui/icons-material/People';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LanguageIcon from '@mui/icons-material/Language';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import PublicAudioPlayer from '../components/public/PublicAudioPlayer';
import ShareButton from '../components/shared/ShareButton';
import { BandRecommendation, BandActivityStatus, getActivityStatusInfo } from '../types/BandRecommendations';
import { trackEvent } from '../utils/analytics';

interface MusicianBand {
  _id: string;
  artistName: string;
  instruments: string[];
  mainImage: string;
  genres: string[];
  activityStatus: BandActivityStatus;
  activityStatusNote?: string;
}

interface SelfInstrument {
  id: string;
  name: string;
  withEffects: boolean;
}

interface MusicianProfile {
  bands: MusicianBand[];
  profileImage: string | null;
  profileImagePosition: string;
  bio: string | null;
  selfGenres: string[];
  selfInstruments: SelfInstrument[];
  website: string | null;
  facebookPage: string | null;
  twitterHandle: string | null;
  instagramHandle: string | null;
  tikTokHandle: string | null;
}

interface SimilarMusician {
  name: string;
  instruments: string[];
  sharedGenres: string[];
  bands: { _id: string; artistName: string; mainImage: string }[];
}

const ACTIVE_STATUSES: BandActivityStatus[] = [
  BandActivityStatus.ACTIVE,
  BandActivityStatus.TOURING,
  BandActivityStatus.RECORDING,
  BandActivityStatus.PROJECT,
];

// Ensure a URL has a protocol prefix
function ensureUrl(url: string): string {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

const MusicianPage: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const { user } = useAuth();
  const isOwnProfile = !!user && user.name === name;
  const [profile, setProfile] = useState<MusicianProfile>({
    bands: [],
    profileImage: null,
    profileImagePosition: 'center center',
    bio: null,
    selfGenres: [],
    selfInstruments: [],
    website: null,
    facebookPage: null,
    twitterHandle: null,
    instagramHandle: null,
    tikTokHandle: null,
  });
  const [similarMusicians, setSimilarMusicians] = useState<SimilarMusician[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);

  useEffect(() => {
    if (!name) return;
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [musicianRes, similarRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/bandrecommendations/musicians/${encodeURIComponent(name)}`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/bandrecommendations/musicians/${encodeURIComponent(name)}/similar`),
        ]);
        const data = musicianRes.data;
        // Backwards compat: plain array response
        if (Array.isArray(data)) {
          setProfile(prev => ({ ...prev, bands: data }));
        } else {
          setProfile({
            bands: data.bands ?? [],
            profileImage: data.profileImage ?? null,
            profileImagePosition: data.profileImagePosition || 'center center',
            bio: data.bio ?? null,
            selfGenres: data.selfGenres ?? [],
            selfInstruments: data.selfInstruments ?? [],
            website: data.website ?? null,
            facebookPage: data.facebookPage ?? null,
            twitterHandle: data.twitterHandle ?? null,
            instagramHandle: data.instagramHandle ?? null,
            tikTokHandle: data.tikTokHandle ?? null,
          });
        }
        setSimilarMusicians(similarRes.data);
        setError(null);
      } catch {
        setError('Failed to load musician information.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [name]);

  useEffect(() => {
    if (!name || loading) return;
    trackEvent('musician_viewed', { musicianName: name });
  }, [name, loading]);

  const { bands, profileImage, profileImagePosition, bio, selfGenres, selfInstruments, website, facebookPage, twitterHandle, instagramHandle, tikTokHandle } = profile;

  // Instruments: self-identified first, then any from bands not already listed
  const allInstruments = useMemo(() => {
    const selfNames = selfInstruments.map(i => i.name);
    const bandInstruments = Array.from(new Set(bands.flatMap(b => b.instruments)));
    const extra = bandInstruments.filter(i => !selfNames.includes(i));
    return [...selfNames, ...extra];
  }, [selfInstruments, bands]);

  // Genres: self-identified first (deduplicated union with band genres)
  const { displayGenres, selfGenreSet } = useMemo(() => {
    const selfSet = new Set(selfGenres);
    const bandGenreCount: Record<string, number> = {};
    bands.forEach(b => b.genres.forEach(g => {
      if (!selfSet.has(g)) bandGenreCount[g] = (bandGenreCount[g] || 0) + 1;
    }));
    const bandGenresSorted = Object.entries(bandGenreCount)
      .sort((a, b) => b[1] - a[1])
      .map(([g]) => g);
    return {
      displayGenres: [...selfGenres, ...bandGenresSorted],
      selfGenreSet: selfSet,
    };
  }, [selfGenres, bands]);

  const hasSocials = website || facebookPage || twitterHandle || instagramHandle || tikTokHandle;
  const heroImage = profileImage || bands[0]?.mainImage || null;
  const hasContent = bands.length > 0 || bio || selfGenres.length > 0 || allInstruments.length > 0;

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return <Container sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }

  return (
    <>
      {/* ── Hero ── */}
      <Box sx={{ position: 'relative', height: { xs: 260, md: 360 }, overflow: 'hidden', mb: 0 }}>
        {heroImage && (
          <Box
            component="img"
            src={heroImage}
            alt={name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: profileImagePosition, filter: 'brightness(0.45)' }}
          />
        )}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)',
        }} />
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1 }}>
            <Box>
              <Typography variant="h2" fontWeight={700} color="white"
                sx={{ fontSize: { xs: '2rem', md: '3rem' }, mb: 1, textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
                {name}
              </Typography>
              {allInstruments.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {allInstruments.map(inst => (
                    <Chip key={inst} icon={<MusicNoteIcon sx={{ fontSize: '0.85rem !important' }} />}
                      label={inst} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', backdropFilter: 'blur(4px)' }} />
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {isOwnProfile && (
                <Button
                  component={Link}
                  to="/artist/profile"
                  size="small"
                  startIcon={<EditIcon />}
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
                >
                  Edit Profile
                </Button>
              )}
              <ShareButton
                url={window.location.href}
                title={name!}
                description={allInstruments.join(', ')}
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>

          {!hasContent && (
            <Alert severity="info">Profile coming soon.</Alert>
          )}

          {/* ── Bio ── */}
          {bio && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="body1" sx={{ lineHeight: 1.8, maxWidth: 720, color: 'text.secondary' }}>
                {bio}
              </Typography>
            </Box>
          )}

          {/* ── Socials / Links ── */}
          {hasSocials && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
              {website && (
                <Chip
                  icon={<LanguageIcon sx={{ fontSize: '1rem !important' }} />}
                  label="Website"
                  component="a"
                  href={ensureUrl(website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  variant="outlined"
                />
              )}
              {facebookPage && (
                <Chip
                  icon={<FacebookIcon sx={{ fontSize: '1rem !important' }} />}
                  label="Facebook"
                  component="a"
                  href={ensureUrl(facebookPage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  variant="outlined"
                />
              )}
              {twitterHandle && (
                <Chip
                  icon={<TwitterIcon sx={{ fontSize: '1rem !important' }} />}
                  label={twitterHandle.startsWith('@') ? twitterHandle : `@${twitterHandle}`}
                  component="a"
                  href={`https://twitter.com/${twitterHandle.replace(/^@/, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  variant="outlined"
                />
              )}
              {instagramHandle && (
                <Chip
                  icon={<InstagramIcon sx={{ fontSize: '1rem !important' }} />}
                  label={instagramHandle.startsWith('@') ? instagramHandle : `@${instagramHandle}`}
                  component="a"
                  href={`https://instagram.com/${instagramHandle.replace(/^@/, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  variant="outlined"
                />
              )}
              {tikTokHandle && (
                <Chip
                  label={tikTokHandle.startsWith('@') ? tikTokHandle : `@${tikTokHandle}`}
                  component="a"
                  href={`https://tiktok.com/@${tikTokHandle.replace(/^@/, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  variant="outlined"
                  sx={{ '& .MuiChip-label': { fontWeight: 500 } }}
                />
              )}
            </Box>
          )}

          {/* ── Genre fingerprint ── */}
          {displayGenres.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2, fontSize: '0.7rem' }}>
                  Styles
                </Typography>
                {selectedGenre && (
                  <Chip label="Clear filter" size="small" onDelete={() => setSelectedGenre(null)}
                    sx={{ fontSize: '0.65rem', height: 20 }} />
                )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {displayGenres.map((genre) => (
                  <Tooltip
                    key={genre}
                    title={
                      selfGenreSet.has(genre)
                        ? `${name} identifies as ${genre}`
                        : selectedGenre === genre ? 'Clear filter' : `Show ${genre} bands`
                    }
                  >
                    <Chip
                      label={genre}
                      size="small"
                      onClick={() => {
                        if (!selfGenreSet.has(genre)) {
                          setSelectedGenre(prev => prev === genre ? null : genre);
                        }
                      }}
                      color="primary"
                      variant={selectedGenre === genre ? 'filled' : 'outlined'}
                      sx={{
                        cursor: selfGenreSet.has(genre) ? 'default' : 'pointer',
                        opacity: selectedGenre && selectedGenre !== genre && !selfGenreSet.has(genre) ? 0.35 : 1,
                        transition: 'opacity 0.2s ease',
                        // Self-identified genres get a subtle highlight
                        ...(selfGenreSet.has(genre) && selectedGenre === null && {
                          borderColor: 'primary.main',
                          bgcolor: 'rgba(25,118,210,0.08)',
                        }),
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          {/* ── Audio player ── */}
          {bands.length > 0 && (
            <MusicianAudioSection name={name!} bandIds={bands.map(b => b._id)} />
          )}

          {/* ── Bands ── */}
          {bands.length > 0 && (
            <>
              {[
                {
                  key: 'active',
                  label: `Bands ${name} Currently Plays In`,
                  sublabel: 'Available for bookings',
                  filter: (b: MusicianBand) => ACTIVE_STATUSES.includes(b.activityStatus),
                },
                {
                  key: 'past',
                  label: `Bands ${name} Has Played In`,
                  sublabel: 'Hiatus, dormant or disbanded',
                  filter: (b: MusicianBand) => !ACTIVE_STATUSES.includes(b.activityStatus),
                },
              ].map(({ key, label, sublabel, filter }) => {
                const group = bands
                  .filter(filter)
                  .filter(b => !selectedGenre || b.genres.includes(selectedGenre));
                if (!group.length) return null;
                return (
                  <Box key={key} sx={{ mb: 5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2 }}>
                      <FiberManualRecordIcon sx={{
                        fontSize: 10,
                        color: key === 'active' ? '#66bb6a' : 'text.disabled',
                        mb: 0.2,
                      }} />
                      <Typography variant="h5" fontWeight={700}>{label}</Typography>
                      <Typography variant="body2" color="text.secondary">{sublabel}</Typography>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                    </Box>
                    <Grid container spacing={2}>
                      {group.map((band) => {
                        const statusInfo = getActivityStatusInfo(band.activityStatus);
                        return (
                          <Grid item xs={12} sm={6} md={4} key={band._id}>
                            <Card sx={{
                              height: '100%', display: 'flex', flexDirection: 'column',
                              opacity: key === 'past' ? 0.78 : 1,
                              transition: 'transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease',
                              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.35)', opacity: 1 },
                            }}>
                              <CardActionArea component={Link} to={`/band/${band._id}`}
                                sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                {band.mainImage && (
                                  <Box sx={{ position: 'relative' }}>
                                    <CardMedia component="img" height={180} image={band.mainImage}
                                      alt={band.artistName} sx={{ objectFit: 'cover' }} />
                                    <Tooltip title={band.activityStatusNote || statusInfo.description}>
                                      <Box sx={{
                                        position: 'absolute', top: 10, right: 10,
                                        bgcolor: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)',
                                        borderRadius: 5, px: 1.2, py: 0.4,
                                        display: 'flex', alignItems: 'center', gap: 0.6,
                                      }}>
                                        <FiberManualRecordIcon sx={{
                                          fontSize: 8,
                                          color: statusInfo.color === 'success' ? '#66bb6a'
                                            : statusInfo.color === 'warning' ? '#ffa726'
                                            : statusInfo.color === 'error' ? '#ef5350'
                                            : '#4fc3f7',
                                        }} />
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1 }}>
                                          {statusInfo.label}
                                        </Typography>
                                      </Box>
                                    </Tooltip>
                                  </Box>
                                )}
                                <CardContent sx={{ flexGrow: 1 }}>
                                  <Typography variant="h6" fontWeight={600} gutterBottom>{band.artistName}</Typography>
                                  <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {band.instruments.map(inst => (
                                      <Chip key={inst} label={inst} size="small"
                                        sx={{ fontSize: '0.72rem', bgcolor: 'rgba(79,195,247,0.1)', color: '#4fc3f7' }} />
                                    ))}
                                  </Box>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {band.genres.slice(0, 3).map(genre => (
                                      <Chip key={genre} label={genre} size="small" variant="outlined" sx={{ fontSize: '0.68rem' }} />
                                    ))}
                                  </Box>
                                </CardContent>
                              </CardActionArea>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                );
              })}
            </>
          )}

          {/* ── Similar Musicians ── */}
          {similarMusicians.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <PeopleIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h5" fontWeight={700}>
                  Other {allInstruments[0] ? `${allInstruments[0]} Players` : 'Musicians'} You Might Explore
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>
              <Box sx={{
                display: 'flex', gap: 2, overflowX: 'auto', pb: 1.5,
                scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent',
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 },
              }}>
                {similarMusicians.map(musician => (
                  <Card key={musician.name} component={Link} to={`/musician/${encodeURIComponent(musician.name)}`}
                    sx={{
                      minWidth: { xs: 170, md: 200 }, maxWidth: { xs: 170, md: 200 },
                      flexShrink: 0, textDecoration: 'none', display: 'block',
                      bgcolor: 'background.paper',
                      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' },
                    }}>
                    {musician.bands[0]?.mainImage && (
                      <CardMedia component="img" height={110}
                        image={musician.bands[0].mainImage} alt={musician.name}
                        sx={{ objectFit: 'cover', filter: 'brightness(0.75)' }} />
                    )}
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ mb: 0.5, color: 'text.primary' }}>
                        {musician.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        {musician.instruments.slice(0, 2).join(', ')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                        {musician.sharedGenres.slice(0, 2).map(g => (
                          <Chip key={g} label={g} size="small"
                            sx={{ fontSize: '0.62rem', height: 17, bgcolor: 'rgba(79,195,247,0.1)', color: '#4fc3f7' }} />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}

        </Box>
      </Container>
    </>
  );
};

// Sub-component: fetches full band data (with youtubeEmbeds) then renders audio player
const MusicianAudioSection: React.FC<{ name: string; bandIds: string[] }> = ({ bandIds }) => {
  const [playerBands, setPlayerBands] = useState<BandRecommendation[]>([]);

  useEffect(() => {
    if (!bandIds.length) return;
    Promise.all(
      bandIds.map(id =>
        axios.get<BandRecommendation>(`${process.env.REACT_APP_API_URL}/api/bandrecommendations/${id}`)
          .then(r => r.data).catch(() => null)
      )
    ).then(results => {
      const valid = results.filter((b): b is BandRecommendation => !!b && (b.youtubeEmbeds?.length ?? 0) > 0);
      setPlayerBands(valid);
    });
  }, [bandIds.join(',')]);

  if (!playerBands.length) return null;

  return <Box sx={{ mb: 4 }}><PublicAudioPlayer bands={playerBands} /></Box>;
};

export default MusicianPage;
